


































import Vue from "vue";

const CSS_VAR_NAME = `--jz-slider-progress`;

interface Props {
  iconLeft: string;
  iconRight: string;
  /**
   * Initial value and v-model
   */
  value: number;
  /**
   * Allowed values
   */
  values: number[];
  /**
   * To define the value when JzInputSlider is loaded. Such function is defined
   * as a prop for two reasons:
   *    - to keep the component functional
   *    - if a custom fallback value is preferred
   */
  valueInit: (values: number[], value: number) => number;
}

interface Computed {
  initialValue: number;
}

interface Method {
  /**
   * Value change listener with extra steps
   */
  onNewValue: (newVal: number) => void;
  /**
   * Adjust the CSS variable for webkit browser
   */
  setWebkitCssVariable: (value: number) => void;
}

/**
 * >>> Input type="range"
 * As of May 2020, datalist are not supported by all browsers.
 *
 * >>> Styling
 * ---- Grid layout ----
 * Grid layout is used with template-areas. If one icon is missing, the column
 * has a width of zero and only the column-gap will be rendered on the top-left
 *
 * ---- Labels ----
 * Labels are not directly linked to the input[type="range"]. To properly position
 * the label, a min-width contraint is added on all labels except the first and
 * last labels. The min-width will improve the horizontal distribution of all
 * elements
 *
 * ---- Slider ----
 * The funniest part. As-of May 2020, there is no standard way to handle such
 * slider. And each vendor specific styling must be adapted
 *  < Webkit (Chrome / Safari) >
 *  - Thumbs must have a proper negative margin-top to center with the track
 *  - There is no fill/progress CSS available. A linear-gradient is used instead
 *    the progress rate is defined by a local CSS variable. Such CSS variable is
 *    then updated by JavaSCript to give the illusion that the progress bar width
 *    changed
 *  < Firefox >
 *  - The three main components (thumb, track, progress) can be styled properly
 *  < Edge >
 *  - Edge splits the track in two parts: upper (after the track) and lower (before
 *    the track) which gives a finer control of the "progress/fill" bar.
 *
 * References:
 * @see https://css-tricks.com/sliding-nightmare-understanding-range-input/
 * @see https://css-tricks.com/styling-cross-browser-compatible-range-inputs-css/
 * @see https://github.com/darlanrod/input-range-scss/blob/master/_inputrange.scss
 */
export default Vue.extend<{}, Method, Computed, Props>({
  name: "JzInputSlider",

  props: {
    iconLeft: { type: String, default: undefined },
    iconRight: { type: String, default: undefined },
    value: { type: Number, required: true },
    values: { type: Array, required: true },
    // uniqueId: {
    //   type: String,
    //   // https://gist.github.com/gordonbrander/2230317
    //   default: () =>
    //     Math.random()
    //       .toString(36)
    //       .substr(2, 9)
    // }
    valueInit: {
      type: Function,
      default: (values: number[], value: number) => {
        // Check if there is an exact match
        let initValue = values.findIndex(opt => opt === value);

        // If no exact match is found, put it in the middle
        if (initValue < 0) {
          initValue = values.length / 2;
        }

        return initValue;
      }
    }
  },

  computed: {
    initialValue() {
      return this.valueInit(this.values, this.value);
    }
  },

  mounted() {
    this.setWebkitCssVariable(this.value);
  },

  methods: {
    onNewValue(newVal) {
      this.$emit("input", newVal);
      this.setWebkitCssVariable(newVal);
    },
    setWebkitCssVariable(value) {
      // find the index of the provided value
      let valueIdx = this.values.findIndex(opt => opt === value);
      // Ensure that we have at least zero
      valueIdx = Math.max(valueIdx, 0);

      const progress = (100 * valueIdx) / (this.values.length - 1);
      (this.$el as HTMLElement).style.setProperty(CSS_VAR_NAME, `${progress}%`);
    }
  }
});
